import { useRef, useState, useEffect, useCallback } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, FileText, Shield, Lock, BookOpen, ZoomIn, ZoomOut, X, Maximize2, User } from "lucide-react";
import { useSectionTracking, useAnalytics } from "./AnalyticsProvider";

const documents = {
  gubelin: {
    title: "Gübelin Gemmological Report",
    subtitle: "Report No. 25070096 — 18 July 2025",
    pages: [
      "/media/docs/gubelin/page_1.jpg",
      "/media/docs/gubelin/page_2.jpg",
      "/media/docs/gubelin/page_3.jpg",
      "/media/docs/gubelin/page_4.jpg",
    ],
    icon: Shield,
  },
  appraisal: {
    title: "Ringsrud Gemology Appraisal",
    subtitle: "Independent Appraisal — January 2026",
    pages: [
      "/media/docs/appraisal/page_1.jpg",
      "/media/docs/appraisal/page_2.jpg",
    ],
    icon: FileText,
  },
  profile: {
    title: "Gübelin Gemological Profile",
    subtitle: "The Premium Profile Book — First for any Raw Emerald",
    pages: Array.from({ length: 32 }, (_, i) => `/media/docs/gubelin_profile/page_${i + 1}.jpg`),
    icon: BookOpen,
  },
  ringsrud: {
    title: "Appraiser Profile",
    subtitle: "Ronald Ringsrud — GIA Graduate Gemologist",
    pages: [
      "/media/docs/ringsrud_profile/page_1.jpg",
      "/media/docs/ringsrud_profile/page_2.jpg",
    ],
    icon: User,
  },
};

function WatermarkOverlay() {
  return (
    <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden opacity-[0.07]">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="absolute text-[#C5A059] font-serif text-lg whitespace-nowrap"
          style={{
            transform: "rotate(-30deg)",
            top: `${i * 14}%`,
            left: "-10%",
            width: "140%",
            textAlign: "center",
            letterSpacing: "0.3em",
          }}
        >
          CONFIDENTIAL — THE BRIDGETTE EMERALD — DO NOT COPY — CONFIDENTIAL — THE BRIDGETTE EMERALD
        </div>
      ))}
    </div>
  );
}

function ZoomModal({ pages, currentPage, onPageChange, onClose }) {
  const [zoom, setZoom] = useState(1);
  const modalRef = useRef(null);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPageChange(Math.max(0, currentPage - 1));
      if (e.key === "ArrowRight") onPageChange(Math.min(pages.length - 1, currentPage + 1));
      if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(3, z + 0.25));
      if (e.key === "-") setZoom((z) => Math.max(0.5, z - 0.25));
      if (e.ctrlKey && (e.key === "s" || e.key === "p")) e.preventDefault();
    };
    window.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [currentPage, pages.length, onPageChange, onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] bg-black/95 flex flex-col select-none"
      style={{ WebkitUserSelect: "none", MozUserSelect: "none", userSelect: "none" }}
      onContextMenu={(e) => e.preventDefault()}
      data-testid="doc-zoom-modal"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 bg-[#111110]/80 border-b border-[#C5A059]/10">
        <span className="text-xs text-[#B8B3AA]/75">
          Page {currentPage + 1} of {pages.length}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
            className="w-8 h-8 flex items-center justify-center text-[#B8B3AA]/70 hover:text-[#C5A059] transition-colors"
            data-testid="zoom-out-btn"
          >
            <ZoomOut size={16} strokeWidth={1.5} />
          </button>
          <span className="text-xs text-[#B8B3AA]/75 w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button
            onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
            className="w-8 h-8 flex items-center justify-center text-[#B8B3AA]/70 hover:text-[#C5A059] transition-colors"
            data-testid="zoom-in-btn"
          >
            <ZoomIn size={16} strokeWidth={1.5} />
          </button>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center text-[#B8B3AA]/70 hover:text-[#F4F0EA] transition-colors"
          data-testid="zoom-close-btn"
        >
          <X size={18} strokeWidth={1.5} />
        </button>
      </div>

      {/* Scrollable zoom area */}
      <div
        ref={modalRef}
        className="flex-1 overflow-auto flex items-start justify-center p-4"
      >
        <div className="relative" style={{ transform: `scale(${zoom})`, transformOrigin: "top center", transition: "transform 0.2s ease" }}>
          <img
            src={pages[currentPage]}
            alt={`Document page ${currentPage + 1}`}
            className="max-w-[90vw] h-auto pointer-events-none"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            style={{ WebkitUserDrag: "none", userDrag: "none", WebkitTouchCallout: "none" }}
          />
          <div className="absolute inset-0 z-10" onContextMenu={(e) => e.preventDefault()} style={{ background: "transparent" }} />
          <WatermarkOverlay />
        </div>
      </div>

      {/* Bottom nav */}
      {pages.length > 1 && (
        <div className="flex items-center justify-center gap-4 px-4 py-3 bg-[#111110]/80 border-t border-[#C5A059]/10">
          <button
            onClick={() => onPageChange(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className="w-10 h-10 flex items-center justify-center text-[#B8B3AA]/75 hover:text-[#C5A059] transition-colors disabled:opacity-20"
            data-testid="zoom-prev"
          >
            <ChevronLeft size={20} strokeWidth={1.5} />
          </button>
          <div className="flex gap-1.5">
            {pages.map((_, i) => (
              <button
                key={i}
                onClick={() => onPageChange(i)}
                className={`h-1.5 transition-all ${i === currentPage ? "bg-[#C5A059] w-6" : "bg-[#B8B3AA]/15 w-2"}`}
              />
            ))}
          </div>
          <button
            onClick={() => onPageChange(Math.min(pages.length - 1, currentPage + 1))}
            disabled={currentPage === pages.length - 1}
            className="w-10 h-10 flex items-center justify-center text-[#B8B3AA]/75 hover:text-[#C5A059] transition-colors disabled:opacity-20"
            data-testid="zoom-next"
          >
            <ChevronRight size={20} strokeWidth={1.5} />
          </button>
        </div>
      )}
    </motion.div>
  );
}

function SecurePageViewer({ pages, currentPage, onPageChange }) {
  const containerRef = useRef(null);
  const [showZoom, setShowZoom] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const prevent = (e) => { e.preventDefault(); return false; };
    el.addEventListener("contextmenu", prevent);
    el.addEventListener("dragstart", prevent);
    return () => {
      el.removeEventListener("contextmenu", prevent);
      el.removeEventListener("dragstart", prevent);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative select-none"
      style={{ WebkitUserSelect: "none", MozUserSelect: "none", userSelect: "none" }}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      {/* Constrained height container */}
      <div className="relative bg-[#1A1918] border border-[#C5A059]/10 p-3 sm:p-4" style={{ boxShadow: "20px 20px 60px rgba(0,0,0,0.5)" }}>
        <div className="relative overflow-hidden aspect-[3/4]">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0"
          >
            <img
              src={pages[currentPage]}
              alt={`Document page ${currentPage + 1}`}
              className="w-full h-full object-contain object-top pointer-events-none"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              style={{ WebkitUserDrag: "none", userDrag: "none", WebkitTouchCallout: "none" }}
            />
            <div className="absolute inset-0 z-10" onContextMenu={(e) => e.preventDefault()} style={{ background: "transparent" }} />
            <WatermarkOverlay />
          </motion.div>
        </AnimatePresence>

        {/* Expand / Zoom button */}
        <button
          onClick={() => setShowZoom(true)}
          className="absolute bottom-3 right-3 z-30 flex items-center gap-2 px-3 py-2 bg-[#111110]/80 border border-[#C5A059]/20 text-[#C5A059]/70 hover:text-[#C5A059] hover:border-[#C5A059]/40 transition-all text-xs"
          data-testid="doc-expand-btn"
        >
          <Maximize2 size={14} strokeWidth={1.5} />
          <span>Expand & Zoom</span>
        </button>

        {/* Navigation */}
        {pages.length > 1 && (
          <>
            <button
              onClick={() => onPageChange(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 flex items-center justify-center bg-[#111110]/70 border border-[#C5A059]/10 text-[#B8B3AA]/75 hover:text-[#C5A059] transition-all disabled:opacity-20"
              data-testid="doc-prev"
            >
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>
            <button
              onClick={() => onPageChange(Math.min(pages.length - 1, currentPage + 1))}
              disabled={currentPage === pages.length - 1}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 flex items-center justify-center bg-[#111110]/70 border border-[#C5A059]/10 text-[#B8B3AA]/75 hover:text-[#C5A059] transition-all disabled:opacity-20"
              data-testid="doc-next"
            >
              <ChevronRight size={18} strokeWidth={1.5} />
            </button>
          </>
        )}
        </div>
      </div>

      {/* Page indicators */}
      <div className="flex items-center justify-between mt-3 px-1">
        <div className="flex items-center gap-2 text-xs text-[#B8B3AA]/65">
          <Lock size={12} strokeWidth={1.5} />
          <span>View only — downloading disabled</span>
        </div>
        <div className="flex gap-1.5">
          {pages.map((_, i) => (
            <button
              key={i}
              onClick={() => onPageChange(i)}
              className={`h-1 transition-all ${i === currentPage ? "bg-[#C5A059] w-5" : "bg-[#B8B3AA]/15 w-1.5"}`}
            />
          ))}
        </div>
        <span className="text-xs text-[#B8B3AA]/35">
          {currentPage + 1} / {pages.length}
        </span>
      </div>

      {/* Zoom Modal */}
      <AnimatePresence>
        {showZoom && (
          <ZoomModal
            pages={pages}
            currentPage={currentPage}
            onPageChange={onPageChange}
            onClose={() => setShowZoom(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default function DocumentViewer() {
  const sectionRef = useSectionTracking("documents");
  const analytics = useAnalytics();
  const animRef = useRef(null);
  const inView = useInView(animRef, { once: true, margin: "-80px" });
  const [activeDoc, setActiveDoc] = useState("gubelin");
  const [currentPage, setCurrentPage] = useState(0);

  const switchDoc = useCallback((docKey) => {
    setActiveDoc(docKey);
    setCurrentPage(0);
    analytics?.trackDocumentView?.(docKey, 0);
  }, [analytics]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    analytics?.trackDocumentView?.(activeDoc, page);
  }, [analytics, activeDoc]);

  // Keyboard shortcut blocking scoped to document section
  useEffect(() => {
    const el = document.getElementById("documents");
    if (!el) return;
    const handleKeyDown = (e) => {
      if (!el.matches(":hover")) return;
      if (
        e.key === "PrintScreen" ||
        (e.ctrlKey && (e.key === "s" || e.key === "S" || e.key === "p" || e.key === "P"))
      ) {
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const doc = documents[activeDoc];
  const docViewerRef = useRef(null);

  const handleSwitchDoc = useCallback((docKey) => {
    switchDoc(docKey);
    // On mobile, scroll the document viewer into view after switching
    if (window.innerWidth < 1024 && docViewerRef.current) {
      setTimeout(() => {
        docViewerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [switchDoc]);

  return (
    <section id="documents" ref={sectionRef} className="relative py-12 lg:py-20" data-testid="documents-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12" ref={animRef}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <p className="overline mb-4">Official Documentation</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F4F0EA] font-light tracking-tighter mb-4">
            Certified <span className="italic text-[#C5A059]">Reports</span>
          </h2>
          <p className="text-base text-[#B8B3AA] max-w-xl font-light leading-relaxed">
            View the original Gübelin Gemmological Report, Ringsrud Gemology Appraisal, and appraiser credentials.
            Documents are protected and available for viewing only.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Tabs + Info — shows FIRST on mobile, RIGHT on desktop */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8 order-1 lg:order-2"
          >
            {/* Active Document Title */}
            <div>
              <h3 className="font-serif text-2xl lg:text-3xl text-[#F4F0EA] mb-3">{doc.title}</h3>
              <p className="text-base text-[#B8B3AA] leading-relaxed font-light">{doc.subtitle}</p>
            </div>

            {/* Document Tabs — vertical list */}
            <div className="space-y-2">
              {Object.entries(documents).map(([key, d]) => {
                const Icon = d.icon;
                return (
                  <button
                    key={key}
                    onClick={() => handleSwitchDoc(key)}
                    data-testid={`doc-tab-${key}`}
                    className={`flex items-center gap-4 w-full px-5 py-4 text-left text-sm transition-all ${
                      activeDoc === key
                        ? "bg-[#C5A059]/10 text-[#C5A059] border border-[#C5A059]/20"
                        : "text-[#B8B3AA]/70 border border-[#C5A059]/5 hover:text-[#B8B3AA]/70 hover:border-[#C5A059]/15"
                    }`}
                  >
                    <Icon size={18} strokeWidth={1.5} />
                    <div>
                      <span className="block font-medium">{d.title}</span>
                      <span className={`block text-xs mt-0.5 ${activeDoc === key ? "text-[#C5A059]/60" : "text-[#B8B3AA]/30"}`}>{d.subtitle}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Security Notice */}
            <div className="border-l-2 border-[#C5A059]/30 pl-6">
              <div className="flex items-center gap-2 mb-3">
                <Shield size={16} strokeWidth={1.5} className="text-[#C5A059]/40" />
                <p className="text-xs text-[#C5A059]/50 uppercase tracking-wider">Confidential</p>
              </div>
              <p className="text-sm text-[#B8B3AA]/70 leading-relaxed">
                These documents are presented under the terms of the Non-Disclosure Agreement you accepted upon registration.
                Reproduction, screenshot, or distribution is strictly prohibited under the governing laws of Switzerland.
              </p>
            </div>
          </motion.div>

          {/* Document Viewer — shows SECOND on mobile, LEFT on desktop */}
          <motion.div
            ref={docViewerRef}
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="order-2 lg:order-1"
          >
            <SecurePageViewer
              pages={doc.pages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
